import { useEffect } from "react";

import { useScribeStore } from "../stores/scribe";
import { isScribeSocketLiveStep } from "../utils/scribeLifecycle";

/**
 * Registers a `beforeunload` listener whenever the scribe socket is live —
 * recording, paused, or finishing — so the browser prompts the user before they
 * close or reload the tab.
 *
 * Closing the tab tears down the Web Worker that hosts the el8-transcribe
 * socket; the socket closes with code 1001 and the in-progress recording is
 * lost. The prompt gives the user a chance to finish the visit first. Gated on
 * the same `isScribeSocketLiveStep` predicate the teardown logic uses, so the
 * guard and the lifecycle can't disagree about when a session is at risk.
 */
export function useWarnOnWindowCloseWhileRecording(): void {
  const step = useScribeStore((s) => s.step);
  const isRecording = isScribeSocketLiveStep(step);

  useEffect(() => {
    if (!isRecording) return;

    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Legacy browsers only show the prompt when returnValue is set.
      event.returnValue = true;
    };

    window.addEventListener("beforeunload", handler);

    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [isRecording]);
}
